import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import PaneBody from 'components/Common/Pane/PaneBody'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { formToObject } from 'lib/formToObject'
import { register } from 'actions/AuthActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import SquareCheckboxInput from 'components/Luxkit/Checkbox/SquareCheckboxInput'
import BodyText from 'components/Luxkit/Typography/BodyText'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import { fireInteractionEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import { signupModalOpen } from 'analytics/eventDefinitions'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import LegalText from './LegalText'
import config from 'constants/config'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'

const AgreeCheckbox = styled(SquareCheckboxInput)``

const StyledForm = styled.form`
  ${AgreeCheckbox} {
    display: flex;
  }
`

const NameContainer = styled.div`
  display: grid;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.xs} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const Footer = styled(PaneBody)`
  display: flex;
  align-items: center;
  justify-content: center;
`

interface PhoneNum {
  phone: string;
  prefix: string;
}

interface JoinForm {
  email: string;
  password: string;
  givenName: string;
  firstName: string;
  phoneNum?: PhoneNum;
  postcode: string;
}

interface Props {
  error?: any;
  regionCode?: string;
  open: boolean;
  email: string;
}

function Join(props: Props) {
  const {
    regionCode,
    error,
    open,
    email,
  } = props

  const firstInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      firstInput.current?.focus()
      fireNonInteractionEvent(signupModalOpen())
    }
  }, [open])

  const termsCheckboxRef = useRef<HTMLInputElement>(null)

  const dispatch = useAppDispatch()

  const fireMobilePhoneInputEvent = (phoneNum?: PhoneNum) => {
    if (phoneNum) {
      fireInteractionEvent({
        category: 'auth-modal',
        action: 'mobile-field',
        label: phoneNum.phone ? 'input' : 'no-input',
      })
    }
  }

  const onJoin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const joinData = formToObject<JoinForm>(e.currentTarget)
    const phoneNum = joinData.phoneNum
    delete joinData.phoneNum

    fireMobilePhoneInputEvent(phoneNum)
    dispatch(register({
      ...joinData,
      ...(phoneNum?.phone && {
        phone: phoneNum.phone,
        phonePrefix: phoneNum.prefix,
      }),
    }, 'email'))
  }

  return (
    <>
      <StyledForm name="joinForm" onSubmit={onJoin}>
        <Group direction="vertical" gap={12}>
          <Group direction="vertical" gap={20}>
            {error?.message && <MessageBanner kind="critical" description={error.message} />}
            <Group direction="horizontal" gap={20}>
              <Group direction="vertical" gap={8}>
                <BodyText forwardedAs="span" variant="large">Let’s create an account for{' '}
                  <BodyText as="span" variant="large" weight="bold">{email}</BodyText>
                </BodyText>
              </Group>
            </Group>
            <NameContainer>
              <TextInput
                id="givenName"
                required
                type="text"
                name="givenName"
                placeholder="First Name"
                autoComplete="given-name"
                requiredErrorMessage="First name is required"
                inputRef={firstInput}
                label="First name"
                pattern="^[^\.\$\%\&\(\)\*!,@#\d]*$"
                invalidErrorMessage="Please enter a valid first name"
                style={{ width: 'auto' }}
              />
              <TextInput
                id="surname"
                required
                type="text"
                name="surname"
                placeholder="Last Name"
                autoComplete="family-name"
                requiredErrorMessage="Last name is required"
                label="Last name"
                pattern="^[^\.\$\%\&\(\)\*!,@#\d]*$"
                invalidErrorMessage="Please enter a valid last name"
                style={{ width: 'auto' }}
              />
            </NameContainer>
          </Group>
          <HiddenInput
            id="email"
            name="email"
            value={email}
          />
          <PhoneInput
            id="phoneNum"
            name="phoneNum"
            label="Phone number"
            prefixCountry={regionCode}
            autoComplete="off"
          />
          {config.signUp.showPostcode && (
            <TextInput
              id="postcode"
              required
              type="text"
              name="postcode"
              placeholder="Postcode"
              autoComplete="off"
              requiredErrorMessage="Postcode is required"
              label="Postcode"
            />
          )}
          <TextInput
            id="password"
            required
            type="password"
            name="password"
            placeholder="Password"
            minLength={8}
            requiredErrorMessage="Password is required"
            label="Create password"
          />
          <AgreeCheckbox
            ref={termsCheckboxRef}
            name="agreeTandC"
            required
          >
            <LegalText />
          </AgreeCheckbox>
          <TextButton
            kind="primary"
            fit="flex"
            type="submit"
            size="large"
          >
            Create account
          </TextButton>
        </Group>
      </StyledForm>
      <Footer></Footer>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(Join)
